/*
 * Description:
 * ---------------------
 * WAN Health Sensing panel — displays per-WAN latency, jitter, loss,
 * measured throughput, and composite health scores.  Includes a toggle
 * for active throughput burst probing.
 *
 * Polls GET /health every 3 s and GET /health/throughput every 5 s.
 *
 * ! file HealthPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanLabel } from "./WanNames";
import { useConfirm } from "./ConfirmModal";

// ── Utilities ────────────────────────────────────────────────────────────────

const fmtMs = (v) => (v != null ? `${v.toFixed(2)} ms` : "—");
const fmtPct = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : "—");
const fmtBps = (v) => {
  if (v == null || v <= 0) return "—";
  const bps = v * 8;
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  if (bps >= 1e3) return `${(bps / 1e3).toFixed(1)} Kbps`;
  return `${bps.toFixed(0)} bps`;
};

// Sampling throughput (Mbps) from the active burst probes. Prefer the
// download-burst measurement (what the balanced health score now weighs);
// fall back to the upload burst. null until a burst sample has arrived.
const samplingMbps = (w) => {
  if (!w) return null;
  if (typeof w.measured_download_mbps === "number") return w.measured_download_mbps;
  if (typeof w.measured_bw_mbps === "number") return w.measured_bw_mbps;
  return null;
};
const fmtMbps = (v) => (v != null ? `${v.toFixed(1)} Mbps` : "—");
const ago = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "never";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 0) return "now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`;
};

const scoreColor = (v) => {
  if (v >= 80) return "#16a34a";
  if (v >= 60) return "#65a30d";
  if (v >= 40) return "#ca8a04";
  if (v >= 20) return "#ea580c";
  return "#dc2626";
};

const scoreBg = (v) => {
  if (v >= 80) return "rgba(22,163,74,0.10)";
  if (v >= 60) return "rgba(101,163,13,0.10)";
  if (v >= 40) return "rgba(202,138,4,0.10)";
  if (v >= 20) return "rgba(234,88,12,0.10)";
  return "rgba(220,38,38,0.10)";
};

const lossBadge = (v) => {
  if (v == null) return { bg: "#f1f5f9", color: "#64748b", label: "—" };
  const pct = v * 100;
  if (pct === 0) return { bg: "#dcfce7", color: "#16a34a", label: "0%" };
  if (pct <= 2) return { bg: "#fefce8", color: "#a16207", label: `${pct.toFixed(1)}%` };
  if (pct <= 10) return { bg: "#fff7ed", color: "#c2410c", label: `${pct.toFixed(1)}%` };
  return { bg: "#fee2e2", color: "#dc2626", label: `${pct.toFixed(1)}%` };
};

// ── Score ring SVG ───────────────────────────────────────────────────────────

function ScoreRing({ value, size = 72, stroke = 6, label }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value || 0));
  const offset = circ - (pct / 100) * circ;
  const color = scoreColor(pct);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)", display: "block" }}>
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none" stroke="#e2e8f0" strokeWidth={stroke}
          />
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none" stroke={color} strokeWidth={stroke}
            strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease" }}
          />
        </svg>
        <span style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: size > 60 ? "1.25rem" : "1rem", fontWeight: 800, color,
        }}>{pct.toFixed(0)}</span>
      </div>
      {label && (
        <span style={{
          fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.08em", color: "#64748b", marginTop: 2,
        }}>{label}</span>
      )}
    </div>
  );
}

// ── Mini sparkline (built from local /health buffer; no new endpoints) ───────
//
// Renders a compact SVG line for a single metric. Designed to coexist with
// older edges that don't yet ship telemetry — every sample comes from the
// same /health response we already poll, so this works regardless of whether
// notabox-server telemetry ingest is reachable or whether the edge has the
// telemetry collector at all.

function MiniSpark({ points, color, yUnit = "", height = 32, width = 140, fmt }) {
  const valid = (points || []).filter((p) => p.value != null && isFinite(p.value));
  if (valid.length < 2) {
    return (
      <div
        style={{
          height,
          width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#cbd5e1",
          fontSize: "0.7rem",
          fontStyle: "italic",
        }}
      >
        building trend…
      </div>
    );
  }
  const xs = valid.map((p) => p.ts);
  const ys = valid.map((p) => p.value);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = 0;
  const yMax = Math.max(...ys, 0.0001) * 1.15 || 1;
  const sx = (x) => 2 + ((x - xMin) / Math.max(1, xMax - xMin)) * (width - 4);
  const sy = (y) => height - 2 - ((y - yMin) / Math.max(0.0001, yMax - yMin)) * (height - 4);
  const d = valid
    .map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.ts).toFixed(1)} ${sy(p.value).toFixed(1)}`)
    .join(" ");
  const fill = `${d} L${sx(xMax).toFixed(1)} ${(height - 2).toFixed(1)} L${sx(xMin).toFixed(1)} ${(height - 2).toFixed(1)} Z`;
  const last = valid[valid.length - 1].value;
  const formatted = fmt ? fmt(last) : `${last.toFixed(1)}${yUnit ? ` ${yUnit}` : ""}`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={width} height={height} style={{ display: "block", flexShrink: 0 }}>
        <path d={fill} fill={color} fillOpacity={0.10} stroke="none" />
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth="1.6"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <span style={{ fontSize: "0.72rem", fontWeight: 700, color, whiteSpace: "nowrap" }}>{formatted}</span>
    </div>
  );
}

// ── Bar chart helper ─────────────────────────────────────────────────────────

function MetricBar({ value, max, color, label, displayValue }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <span style={{
        fontSize: "0.72rem", fontWeight: 600, color: "#64748b",
        minWidth: 64, textAlign: "right",
      }}>{label}</span>
      <div style={{
        flex: 1, height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%", borderRadius: 4,
          background: color,
          transition: "width 0.5s ease",
        }} />
      </div>
      <span style={{
        fontSize: "0.75rem", fontWeight: 700, color: "#334155",
        minWidth: 70, textAlign: "right",
      }}>{displayValue}</span>
    </div>
  );
}

// ── WAN Card ─────────────────────────────────────────────────────────────────

// What each line is doing for the traffic, from the box's own answer for every
// category (App's lineRouting): the line a category is held to, or the lines it
// bonds across. Never from the health ranking: the best-scored line is not the
// one in use when the operator's order puts another first.
//   PRIMARY  the one line every held category is on
//   IN USE   held categories are split, and some are on this line
//   BONDED   carries bonded categories and no held one
//   BACKUP   up and carrying nothing; takes over when a line in use fails
// Returns a lookup by line name, or null when the box has not answered.
export function lineRoles(routing) {
  if (!routing) return null;
  const held = new Set(routing.filter((c) => !c.bond).flatMap((c) => c.lines));
  const bonded = new Set(routing.filter((c) => c.bond).flatMap((c) => c.lines));
  return (name) => {
    if (held.has(name)) return held.size === 1 ? "PRIMARY" : "IN USE";
    return bonded.has(name) ? "BONDED" : "BACKUP";
  };
}

const ROLE_ORDER = ["PRIMARY", "IN USE", "BONDED", "BACKUP"];

const CHIPS = {
  "NO ACK": { bg: "#fee2e2", fg: "#b91c1c", border: "#fca5a5", title: "No health probe answered in the last 15 s. The line is probably down." },
  DEGRADED: { bg: "#fef3c7", fg: "#a16207", border: "#fcd34d", title: "The health probes see loss or delay past the limits." },
  PRIMARY: { bg: "#dcfce7", fg: "#15803d", border: "#86efac", title: "Your traffic goes out this line right now, as Traffic Shaping sets it." },
  "IN USE": { bg: "#ccfbf1", fg: "#0f766e", border: "#5eead4", title: "Some kinds of traffic go out this line right now, others another." },
  BONDED: { bg: "#e0e7ff", fg: "#4338ca", border: "#a5b4fc", title: "Carries bonded traffic, combined with the other lines." },
  BACKUP: { bg: "#e0f2fe", fg: "#0369a1", border: "#7dd3fc", title: "Ready, carrying nothing. Takes over if a line in use fails." },
};

// The card's chip. A health problem comes first, since a line that stopped
// answering is the news whatever it was doing; then its role. With no role
// (a phone, a box that has not answered, direct mode) a healthy line gets no
// chip rather than a guess.
function statusChip(wan, role) {
  const ack = wan?.last_probe_ack ? new Date(wan.last_probe_ack) : null;
  const ackAgeSec = ack && !isNaN(ack.getTime()) && ack.getFullYear() > 2000
    ? Math.floor((Date.now() - ack.getTime()) / 1000)
    : Infinity;
  const label = ackAgeSec > 15 ? "NO ACK" : wan?.degraded ? "DEGRADED" : role;
  return label ? { label, ...CHIPS[label] } : null;
}

function WANCard({ wan, maxLatency, maxThroughput, history, role }) {
  const loss = lossBadge(wan.loss_ratio);
  const tputMbps = samplingMbps(wan);

  // Build per-metric series from the local history buffer. Each entry is
  // { ts, latency, jitter, loss } pushed by the parent on every /health poll.
  // We tolerate missing fields so legacy edges that don't report a value still
  // get clean gaps instead of broken charts.
  const latPoints = useMemo(
    () => (history || []).map((h) => ({ ts: h.ts, value: h.latency })),
    [history]
  );
  const jitPoints = useMemo(
    () => (history || []).map((h) => ({ ts: h.ts, value: h.jitter })),
    [history]
  );
  const losPoints = useMemo(
    () => (history || []).map((h) => ({ ts: h.ts, value: h.loss })),
    [history]
  );
  const tputPoints = useMemo(
    () => (history || []).map((h) => ({ ts: h.ts, value: h.tput })),
    [history]
  );

  return (
    <div className="health-wan-card">
      {/* Header */}
      <div className="health-wan-header">
        <div className="health-wan-name-row">
          <span className="health-wan-name"><WanLabel iface={wan.name} /></span>
          <span className="health-wan-idx">WAN {wan.index}</span>
          {(() => {
            const b = statusChip(wan, role);
            return b && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: b.bg,
                  color: b.fg,
                  border: `1px solid ${b.border}`,
                  marginLeft: 8,
                  whiteSpace: "nowrap",
                }}
                title={b.title}
              >
                {b.label}
              </span>
            );
          })()}
        </div>
        {/* Degraded-reason hint. Surfaces the root cause + duration directly
            under the name row so operators don't need to drill into the API
            response to understand why a WAN flipped (and for how long).
            Pairs with the symmetric fast-recovery path in pkg/health: a WAN
            that's been "stuck" >2 min on a transient blip is the signal that
            something else (asymmetric routing, ARP issue) is keeping the
            return path broken. */}
        {wan.degraded && (wan.degraded_reason || wan.degraded_since) && (
          <div
            style={{
              fontSize: "0.7rem",
              color: "#a16207",
              marginTop: 4,
              lineHeight: 1.3,
            }}
            title="From /health: reason for degraded state and how long it has been so. Fast-degrade fires on 3 consecutive missed probes; fast-recovery requires 3 consecutive ACKs."
          >
            {wan.degraded_reason && (
              <span style={{ fontWeight: 600 }}>{wan.degraded_reason}</span>
            )}
            {wan.degraded_since && (
              <span style={{ marginLeft: wan.degraded_reason ? 6 : 0, opacity: 0.85 }}>
                · for {ago(wan.degraded_since)}
              </span>
            )}
            {wan.last_probe_ack && (
              <span style={{ marginLeft: 6, opacity: 0.65 }}>
                · last ACK {ago(wan.last_probe_ack)}
              </span>
            )}
          </div>
        )}
        <div className="health-wan-scores">
          <ScoreRing value={wan.score} size={68} label="Overall" />
        </div>
      </div>

      {/* Metrics */}
      <div className="health-metrics-grid">
        {/* Latency */}
        <div className="health-metric-box">
          <div className="health-metric-icon" style={{ background: "rgba(99,102,241,0.10)", color: "#6366f1" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div>
            <div className="health-metric-label">Latency</div>
            <div className="health-metric-value">{fmtMs(wan.latency_ms)}</div>
          </div>
        </div>

        {/* Jitter */}
        <div className="health-metric-box">
          <div className="health-metric-icon" style={{ background: "rgba(139,92,246,0.10)", color: "#8b5cf6" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <div className="health-metric-label">Jitter</div>
            <div className="health-metric-value">{fmtMs(wan.jitter_ms)}</div>
          </div>
        </div>

        {/* Loss */}
        <div className="health-metric-box">
          <div className="health-metric-icon" style={{ background: loss.bg, color: loss.color }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div>
            <div className="health-metric-label">Packet Loss</div>
            <div className="health-metric-value" style={{ color: loss.color }}>{loss.label}</div>
          </div>
        </div>

        {/* Live carried rate — the real passive rate this WAN is moving right
            now. Distinct from the sampling throughput below (an active burst
            measurement); this is what the link is actually carrying. */}
        <div className="health-metric-box">
          <div className="health-metric-icon" style={{ background: "rgba(34,197,94,0.10)", color: "#16a34a" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div>
            <div className="health-metric-label">Live Rate</div>
            <div className="health-metric-value">
              {fmtBps(Math.max(wan.tx_bytes_per_sec || 0, wan.rx_bytes_per_sec || 0))}
            </div>
          </div>
        </div>
      </div>

      {/* Bar charts */}
      <div className="health-bars">
        <MetricBar
          value={wan.latency_ms || 0} max={maxLatency || 100}
          color="#6366f1" label="Latency" displayValue={fmtMs(wan.latency_ms)}
        />
        <MetricBar
          value={(wan.loss_ratio || 0) * 100} max={10}
          color="#dc2626" label="Loss" displayValue={loss.label}
        />
        <MetricBar
          value={wan.jitter_ms || 0} max={maxLatency / 2 || 20}
          color="#8b5cf6" label="Jitter" displayValue={fmtMs(wan.jitter_ms)}
        />
        <MetricBar
          value={tputMbps || 0} max={maxThroughput || 100}
          color="#16a34a" label="Throughput" displayValue={fmtMbps(tputMbps)}
        />
      </div>

      {/* Profile scores */}
      <div className="health-profiles">
        <div className="health-profile-chip" style={{ background: scoreBg(wan.score_latency), color: scoreColor(wan.score_latency) }}>
          <span className="health-profile-label">Latency</span>
          <span className="health-profile-score">{wan.score_latency?.toFixed(0) ?? "—"}</span>
        </div>

        <div className="health-profile-chip" style={{ background: scoreBg(wan.score_balanced), color: scoreColor(wan.score_balanced) }}>
          <span className="health-profile-label">Balanced</span>
          <span className="health-profile-score">{wan.score_balanced?.toFixed(0) ?? "—"}</span>
        </div>
      </div>

      {/* Passive traffic */}
      <div className="health-passive">
        <div className="health-passive-row">
          <span className="health-passive-label">TX</span>
          <span className="health-passive-value">{fmtBps(wan.tx_bytes_per_sec)}</span>
          <span className="health-passive-sep">·</span>
          <span className="health-passive-value">{wan.tx_pkt_per_sec?.toFixed(0) ?? "—"} pkt/s</span>
        </div>
        <div className="health-passive-row">
          <span className="health-passive-label">RX</span>
          <span className="health-passive-value">{fmtBps(wan.rx_bytes_per_sec)}</span>
          <span className="health-passive-sep">·</span>
          <span className="health-passive-value">{wan.rx_pkt_per_sec?.toFixed(0) ?? "—"} pkt/s</span>
        </div>
      </div>

      {/* Trend (built locally from /health polls — works on legacy edges) */}
      <div
        style={{
          padding: "0.55rem 0",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          <span>Trend (last ~6 min)</span>
          <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "#94a3b8" }}>
            {history && history.length > 0 ? `${history.length} samples` : "no data yet"}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            rowGap: 4,
            columnGap: 8,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "0.7rem", color: "#475569", fontWeight: 600 }}>Latency</span>
          <MiniSpark points={latPoints} color="#6366f1" yUnit="ms" />
          <span style={{ fontSize: "0.7rem", color: "#475569", fontWeight: 600 }}>Jitter</span>
          <MiniSpark points={jitPoints} color="#8b5cf6" yUnit="ms" />
          <span style={{ fontSize: "0.7rem", color: "#475569", fontWeight: 600 }}>Loss</span>
          <MiniSpark
            points={losPoints}
            color="#dc2626"
            yUnit="%"
            fmt={(v) => `${v.toFixed(1)}%`}
          />
          <span style={{ fontSize: "0.7rem", color: "#475569", fontWeight: 600 }}>Throughput</span>
          <MiniSpark
            points={tputPoints}
            color="#16a34a"
            yUnit=" Mbps"
            fmt={(v) => `${v.toFixed(1)} Mbps`}
          />
        </div>
      </div>

      {/* Timestamps */}
      <div className="health-footer">
        <span>Last probe ack: {ago(wan.last_probe_ack)}</span>
      </div>
    </div>
  );
}

// ── Active Probing Controls ──────────────────────────────────────────────────

// Probe cadences, in seconds. A circuit that goes dark with its link up is
// caught after three probes in a row go unanswered, so the cadence is the
// failover time: a few seconds at the standard rates, under a second at the
// extreme ones. The extreme rates are a separate group with a warning because
// they cost that much more probe traffic on every circuit and on the anchor,
// and a slow circuit can be judged dark. The appliance refuses anything
// outside 0.1 to 5 s and keeps the choice across restarts.
const STANDARD_CADENCES = [
  { label: "1s", value: 1 },
  { label: "2s", value: 2 },
  { label: "5s", value: 5 },
];
const EXTREME_CADENCES = [
  { label: "0.2s", value: 0.2 },
  { label: "0.1s", value: 0.1 },
];
const isExtremeCadence = (sec) => sec < 1;
const probesPerSecond = (sec) => Math.round(1 / sec);

// What an extreme cadence costs, for the confirmation and for the notice that
// stays on screen while it runs. The figures are the cadence's own; the
// detection window comes from the appliance once the cadence is in force.
function extremeCost(sec) {
  const rate = probesPerSecond(sec);
  return `Every circuit is probed ${rate} times a second, ${rate} times the standard rate, so a circuit that goes dark with its link up is caught in under a second instead of a few seconds. The cost is that much more probe traffic on every circuit and on the anchor, and a circuit that takes longer than the detection window to answer, such as satellite or congested cellular, is judged dark. For a line that must not go quiet. The appliance keeps this setting across restarts.`;
}

const fmtSec = (s) => (Math.round(s * 100) / 100).toString();

// Cadence options for the opt-in throughput bursts. Backend clamps to a 5s
// floor (see Prober.SetBurstInterval); these are the operator-facing presets.
const BURST_INTERVALS = [
  { label: "10s", value: 10 },
  { label: "15s", value: 15 },
  { label: "30s", value: 30 },
  { label: "1 min", value: 60 },
  { label: "2 min", value: 120 },
  { label: "5 min", value: 300 },
];

export function ActiveProbingControl({ config, throughputToggling, onThroughputToggle, onConfigChange }) {
  const confirm = useConfirm();
  const tpEnabled = config?.throughput_enabled ?? false;
  const cadence = config?.probe_interval_sec ?? 1;
  const siteDefault = config?.default_probe_interval_sec ?? 1;
  const extreme = isExtremeCadence(cadence);
  const window = config?.detection_window_sec;
  const listed = [...STANDARD_CADENCES, ...EXTREME_CADENCES].some((o) => o.value === cadence);

  const chooseCadence = async (sec) => {
    if (isExtremeCadence(sec) && !extreme) {
      const ok = await confirm({
        title: "Switch to extreme failover?",
        message: extremeCost(sec),
        confirmLabel: "Switch",
        variant: "danger",
      });
      if (!ok) return;
    }
    onConfigChange({ probe_interval_sec: sec });
  };
  const cadenceOption = (o) => (
    <option key={o.value} value={o.value}>
      every {o.label}{o.value === siteDefault ? " (site default)" : ""}
    </option>
  );

  return (
    <div className="health-tp-toggle">
      <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        {/* ── Tier 1: base loss/latency probing — always on ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div className="health-tp-toggle-text">
            <div className="health-tp-toggle-title">Base Health Probing</div>
            <div className="health-tp-toggle-desc">
              Latency, jitter and packet-loss probes run end-to-end to the cloud anchor — the primary failover signal.
              Detects an ISP/backhaul outage even when the cable stays plugged. Always on.
            </div>
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {extreme && (
              <span
                title={`Probing ${probesPerSecond(cadence)} times a second on every circuit`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  fontSize: "0.72rem", fontWeight: 700, color: "#b45309",
                  background: "#fef3c7", border: "1px solid #fcd34d",
                  padding: "3px 10px", borderRadius: 999,
                }}
              >
                Extreme failover · high probing rate
              </span>
            )}
            <span
              title="Base probing is always active and cannot be turned off"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                fontSize: "0.72rem", fontWeight: 700, color: "#15803d",
                background: "#dcfce7", border: "1px solid #86efac",
                padding: "3px 10px", borderRadius: 999,
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: 999, background: "#22c55e", display: "inline-block" }} />
              Always on
            </span>
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b" }}>Probe cadence:</span>
          <select
            aria-label="Probe cadence"
            value={cadence}
            onChange={(e) => chooseCadence(Number(e.target.value))}
            style={{
              fontSize: "0.75rem", padding: "3px 8px", borderRadius: 6,
              border: `1px solid ${extreme ? "#fcd34d" : "#cbd5e1"}`, background: "#fff", color: "#334155",
              fontWeight: 600, cursor: "pointer",
            }}
          >
            <optgroup label="Standard">{STANDARD_CADENCES.map(cadenceOption)}</optgroup>
            <optgroup label="Extreme failover (high probing rate)">{EXTREME_CADENCES.map(cadenceOption)}</optgroup>
            {!listed && <option value={cadence}>every {cadence}s</option>}
          </select>
          {window && (
            <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
              a circuit that goes dark with its link up is caught in {fmtSec(window.earliest)} to {fmtSec(window.latest)} s
            </span>
          )}
        </div>

        {extreme && (
          <div
            role="note"
            style={{
              fontSize: "0.72rem", color: "#92400e", background: "#fffbeb",
              border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 10px", lineHeight: 1.45,
            }}
          >
            <strong>Extreme failover.</strong> {extremeCost(cadence)}
          </div>
        )}

        {/* ── Tier 2: opt-in throughput / bandwidth-collapse detection ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          paddingTop: 10, borderTop: "1px solid #e2e8f0",
        }}>
          <div className="health-tp-toggle-text">
            <div className="health-tp-toggle-title">
              Bandwidth-Collapse Detection <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#94a3b8" }}>· advanced</span>
            </div>
            <div className="health-tp-toggle-desc">
              Second failover tier. Adds periodic throughput bursts so a link whose bandwidth collapses while small
              probes still pass (ISP shaping/throttling, MTU black holes) also fails over. May be noisy on links behind
              NAT / CGNAT or sharing a public IP — enable only where needed.
            </div>
          </div>
          <button
            type="button"
            className={`health-toggle-btn ${tpEnabled ? "on" : "off"}`}
            disabled={throughputToggling}
            onClick={onThroughputToggle}
            aria-label={tpEnabled ? "Disable bandwidth-collapse detection" : "Enable bandwidth-collapse detection"}
          >
            <span className="health-toggle-knob" />
          </button>
        </div>

        {/* Burst cadence — only relevant while the throughput tier is armed. */}
        {tpEnabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b" }}>Burst interval:</span>
            <select
              value={config?.burst_interval_sec ?? 30}
              onChange={(e) => onConfigChange({ burst_interval_sec: Number(e.target.value) })}
              style={{
                fontSize: "0.75rem", padding: "3px 8px", borderRadius: 6,
                border: "1px solid #cbd5e1", background: "#fff", color: "#334155",
                fontWeight: 600, cursor: "pointer",
              }}
            >
              {BURST_INTERVALS.map((o) => (
                <option key={o.value} value={o.value}>every {o.label}</option>
              ))}
            </select>
            <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
              how often throughput bursts run per WAN
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

export default function HealthPanel({ nodeApiUrl, selectedNode, lineRouting = null }) {
  const [wans, setWans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetch, setLastFetch] = useState(null);
  const [probingConfig, setProbingConfig] = useState(null);
  const [tpToggling, setTpToggling] = useState(false);

  // Per-WAN history buffer for in-card sparklines. Keyed by WAN name; each
  // entry is an array of { ts, latency, jitter, loss } pushed on every /health
  // poll. Capped at HISTORY_CAP samples (~6 min at 3s cadence).
  const HISTORY_CAP = 120;
  const [historyByWan, setHistoryByWan] = useState({});

  // ── Health polling — AbortController prevents stale cross-node data ──
  useEffect(() => {
    if (!selectedNode) { setWans([]); setLoading(false); setHistoryByWan({}); return; }

    const controller = new AbortController();
    setWans([]);
    setHistoryByWan({}); // start fresh on node switch
    setLoading(true);

    const doFetch = async () => {
      const url = nodeApiUrl("/health");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (!controller.signal.aborted) {
          const list = Array.isArray(data?.health) ? data.health : [];
          setWans(list);
          setError("");
          setLastFetch(new Date());

          // Append to per-WAN history. Tolerate any field being absent —
          // older edges may not populate jitter/loss; we just store null.
          const now = Date.now();
          setHistoryByWan((prev) => {
            const next = { ...prev };
            const present = new Set();
            for (const w of list) {
              if (!w?.name) continue;
              present.add(w.name);
              const arr = next[w.name] ? next[w.name].slice() : [];
              arr.push({
                ts: now,
                latency: typeof w.latency_ms === "number" ? w.latency_ms : null,
                jitter: typeof w.jitter_ms === "number" ? w.jitter_ms : null,
                loss:
                  typeof w.loss_ratio === "number" && isFinite(w.loss_ratio)
                    ? w.loss_ratio * 100
                    : null,
                tput: samplingMbps(w),
              });
              if (arr.length > HISTORY_CAP) arr.splice(0, arr.length - HISTORY_CAP);
              next[w.name] = arr;
            }
            // Drop history for WANs that disappeared
            for (const k of Object.keys(next)) {
              if (!present.has(k)) delete next[k];
            }
            return next;
          });
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[HealthPanel] fetch error:", err);
        if (!controller.signal.aborted) {
          setError(err.message || "Failed to load health data");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    doFetch();
    const timer = setInterval(doFetch, 3000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]);

  // ── Probing config polling ──
  const fetchProbingConfig = useCallback(async () => {
    if (!selectedNode) return;
    const url = nodeApiUrl("/health/probing");
    if (!url) return;
    try {
      const res = await fetchWithTimeout(url);
      if (!res.ok) return;
      const data = await res.json();
      setProbingConfig(data);
    } catch { /* non-critical */ }
  }, [nodeApiUrl, selectedNode]);

  useEffect(() => {
    if (!selectedNode) return;
    fetchProbingConfig();
    const timer = setInterval(fetchProbingConfig, 5000);
    return () => clearInterval(timer);
  }, [selectedNode, fetchProbingConfig]);

  const handleThroughputToggle = async () => {
    const url = nodeApiUrl("/health/throughput");
    if (!url) return;
    const newEnabled = !(probingConfig?.throughput_enabled ?? false);
    try {
      setTpToggling(true);
      const res = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: newEnabled }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      // /health/throughput returns only {enabled}; refetch the full probing
      // config so the panel reflects the new throughput-tier state.
      await fetchProbingConfig();
    } catch (err) {
      console.error("[HealthPanel] throughput toggle error:", err);
    } finally {
      setTpToggling(false);
    }
  };

  const handleConfigChange = async (patch) => {
    const url = nodeApiUrl("/health/probing");
    if (!url) return;
    try {
      const res = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setProbingConfig(data);
    } catch (err) {
      console.error("[HealthPanel] config change error:", err);
    }
  };

  // Computed max for bars
  const maxLatency = useMemo(() => Math.max(20, ...wans.map((w) => w.latency_ms || 0)), [wans]);
  const maxThroughput = useMemo(
    () => Math.max(10, ...wans.map((w) => samplingMbps(w) || 0)),
    [wans]
  );

  // The line in use first, then by health score.
  const roleOf = useMemo(() => lineRoles(lineRouting), [lineRouting]);
  const sorted = useMemo(() => {
    const rank = (w) => (roleOf ? ROLE_ORDER.indexOf(roleOf(w.name)) : 0);
    return [...wans].sort((a, b) => rank(a) - rank(b) || (b.score || 0) - (a.score || 0));
  }, [wans, roleOf]);

  if (!selectedNode) {
    return (
      <div className="panel health-panel">
        <div className="panel-title">WAN Health Sensing</div>
        <div className="panel-sub">Select an edge node to view WAN health metrics.</div>
      </div>
    );
  }

  return (
    <div className="panel health-panel">
      <div className="health-panel-header">
        <div>
          <div className="panel-title">WAN Health Sensing</div>
          <div className="panel-sub">
            Active probing &amp; passive sensing per WAN interface
            <span className="health-refresh-ts">{lastFetch ? ` · refreshed ${ago(lastFetch)}` : "\u00A0"}</span>
          </div>
        </div>
      </div>

      <div className="panel-error" style={{ display: error ? undefined : "none" }}>{error}</div>

      {/* Active probing controls, once the node has said how it probes. A
          phone does not serve its prober settings, and controls drawn from
          defaults it never reported would change nothing. */}
      {probingConfig && (
        <ActiveProbingControl
          config={probingConfig}
          throughputToggling={tpToggling}
          onThroughputToggle={handleThroughputToggle}
          onConfigChange={handleConfigChange}
        />
      )}

      <div className="health-wan-grid" style={{ minHeight: 200 }}>
        {loading && wans.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", gridColumn: "1 / -1" }}>Loading health data…</div>
        ) : sorted.length === 0 && !error ? (
          <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", gridColumn: "1 / -1" }}>
            No WAN health data available. Ensure the edge is running with health sensing enabled.
          </div>
        ) : (
          sorted.map((wan) => (
            <WANCard
              key={wan.index}
              wan={wan}
              maxLatency={maxLatency}
              maxThroughput={maxThroughput}
              history={historyByWan[wan.name]}
              role={roleOf?.(wan.name)}
            />
          ))
        )}
      </div>
    </div>
  );
}
